import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { StakingProcess, StakingActionType } from "@modules/staking/domain/entities/staking-process.model";
import { StakingFlowStep } from "@modules/shared/domain/entities/trade-flow.model";
import { TradeAmountType } from "@modules/shared/domain/entities/trade-amount-type.model";
import { convertFiatToTradable } from "@helpers/tradable-convert";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { StakingHelperService } from "@modules/staking/domain/+state/utility/staking-helper.service";
import { Injectable } from "@angular/core";
import { TRADABLE_PRECISION } from "../../entities/constants";

@Injectable()
export class StakingAmountValidationService {
	constructor(
		private readonly stakingFacade: StakingFacade,
		private readonly tradableWalletService: TradableWalletListService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly stakingHelperService: StakingHelperService
	) {}

	public hasFundsToStake(stakeable: Stakeable): boolean {
		const defaultWallet = this.tradableWalletService
			.getWalletsByTradableId(stakeable?.id)
			?.find((wallet: TradableWallet) => wallet.isDefault());
		if (defaultWallet) {
			return defaultWallet.getBalance() >= +stakeable.minStakingAmount;
		}
		return false;
	}

	public validateStakingAmount(process: StakingProcess): void {
		let hasError = false;
		const { amount } = process;
		this.stakingFacade.updateStakingProcess({ error: hasError });
		if (this.isAmountTooLow(process)) {
			hasError = true;
			this.stakingFacade.updateStakingProcess({ error: hasError });
			this.infoService.setError(this.translationService.translate("transaction_amount_low"));
		}
		if (+amount > this.getAvailableAmount(process)) {
			hasError = true;
			this.stakingFacade.updateStakingProcess({ error: hasError });
			this.infoService.setError(this.translationService.translate("staking_more_than_available_error"));
		}
		if (isNaN(+amount)) {
			hasError = true;
			this.stakingFacade.updateStakingProcess({ error: hasError });
			this.infoService.setError(this.translationService.translate("action_required"));
		}
		if (!hasError) {
			this.stakingFacade.goToStep(StakingFlowStep.SUMMARY);
		}
	}

	public isAmountTooLow(process: StakingProcess): boolean {
		const stakeType: StakingActionType = process.type;
		const { amountType, amount } = process;
		// In case amountType is FIAT, amount is converted into tradable-amount
		const validationAmount =
			amountType === TradeAmountType.ASSET ? +amount : this.getValueInTradable(process, +amount);
		// Field minStakingAmount is returned in tradable-amount
		return stakeType === StakingActionType.STAKE
			? validationAmount < +process.stakeable.minStakingAmount
			: +amount <= 0;
	}

	private getValueInTradable(process: StakingProcess, valueInFiat: number): number {
		return convertFiatToTradable(
			this.stakingHelperService.getFiat(),
			valueInFiat,
			this.stakingHelperService.getTradable(process)
		);
	}

	private getAvailableAmount(process: StakingProcess): number {
		const balance: number =
			process.type === StakingActionType.STAKE ? process.wallet.getBalance() : +process.stakeable.stakedAmount;
		if (process.amountType === TradeAmountType.ASSET) {
			return +balance.toFixed(TRADABLE_PRECISION);
		}
		return this.stakingHelperService.getFiatValue(process, balance);
	}
}
